"use client";

import React, { useState } from "react";
import { KeyRound, Lock, Eye, EyeOff, ShieldCheck, X, AlertCircle, Loader2 } from "lucide-react";
import { useToast } from "../ui/Toast";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  React.useEffect(() => {
    if (isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrent(false);
      setShowNew(false);
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          revokeOtherSessions: revokeOthers,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success("Password Updated", "Your administrator password has been updated securely.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        onClose();
      } else {
        setError(data.error || "Failed to update password");
      }
    } catch {
      setError("Network error updating password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-surface-overlay backdrop-blur-sm animate-fade-in">
      <div className="bg-surface-card border border-themed-border rounded-xl max-w-md w-full p-6 shadow-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-themed-textSecondary hover:text-themed-textPrimary p-1 rounded-lg hover:bg-surface-muted"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-lg bg-[var(--accent-primary)]/10 border border-[var(--border-accent)] text-[var(--accent-primary)]">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-themed-textPrimary">Change Password</h3>
            <p className="text-xs text-themed-textSecondary">Update your administrator account password</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-status-danger-bg border border-status-danger-border text-status-danger text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">Current Password</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full bg-surface-root border border-themed-border rounded-lg px-3.5 py-2 text-xs text-themed-textPrimary focus:outline-none focus:border-[var(--accent-primary)] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-2.5 text-themed-textMuted hover:text-themed-textSecondary"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full bg-surface-root border border-themed-border rounded-lg px-3.5 py-2 text-xs text-themed-textPrimary focus:outline-none focus:border-[var(--accent-primary)] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-2.5 text-themed-textMuted hover:text-themed-textSecondary"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block text-xs font-medium text-themed-textSecondary mb-1.5">Confirm New Password</label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full bg-surface-root border border-themed-border rounded-lg px-3.5 py-2 text-xs text-themed-textPrimary focus:outline-none focus:border-[var(--accent-primary)]"
            />
          </div>

          {/* Revoke other sessions */}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="revokeOthers"
              checked={revokeOthers}
              onChange={(e) => setRevokeOthers(e.target.checked)}
              className="rounded bg-surface-root border-themed-border text-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/30 h-4 w-4"
            />
            <label htmlFor="revokeOthers" className="text-xs text-themed-textSecondary cursor-pointer select-none">
              Sign out of all other active sessions/devices
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-themed-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-surface-muted hover:bg-surface-muted text-themed-textSecondary text-xs font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-primary)] text-[var(--accent-text)] text-xs font-bold transition-all disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              <span>Update Password</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
